import React, { useState, useEffect } from 'react'
import {
  Page,
  Layout,
  Card,
  Box,
  Text,
  Badge,
  ProgressBar,
  BlockStack,
  InlineStack,
  InlineGrid,
  Button,
  Divider,
  SkeletonBodyText,
  SkeletonDisplayText,
  Toast,
} from '@shopify/polaris'
import { ChevronLeftIcon, CheckIcon, XIcon } from '@shopify/polaris-icons'
import { useQuery, useMutation } from 'react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, formatDistance } from 'date-fns'

import { getJobDetails, triggerJob } from '../../../apis/jobs'
import { JobStatus, JobStatusConfig, needsRefresh } from '../../../constants/jobStatus'

export default function JobDetails() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [refreshInterval, setRefreshInterval] = useState(null)
  const [toastActive, setToastActive] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastError, setToastError] = useState(false)

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['job', id],
    queryFn: () => getJobDetails(id),
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: true,
    staleTime: refreshInterval ? 0 : 5 * 60 * 1000, // No cache when auto-refreshing
    cacheTime: refreshInterval ? 0 : 5 * 60 * 1000, // No cache when auto-refreshing
  })

  const triggerJobMutation = useMutation(triggerJob, {
    onSuccess: () => {
      setToastMessage(t('Jobs.details.triggerSuccess'))
      setToastError(false)
      setToastActive(true)
      refetch()
    },
    onError: (error) => {
      setToastMessage(error.message || t('Jobs.details.triggerError'))
      setToastError(true)
      setToastActive(true)
    },
  })

  const job = data?.job
  const events = data?.events || []

  // Format job type for display
  const formatJobType = (type) => {
    const translationKey = `Jobs.types.${type}`
    const translated = t(translationKey)
    if (translated !== translationKey) {
      return translated
    }
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  // Format job results based on job type
  const formatJobResult = (job) => {
    if (!job.result || Object.keys(job.result).length === 0) return null
    
    // Job types that should not show results
    const hideResultTypes = [
      'webhook.webPixelUpdate',
      'storefront.createAccessToken',
      'web_pixel',
      'storefront_token'
    ]
    
    if (hideResultTypes.includes(job.type)) {
      return null
    }
    
    // Format specific job types
    const formattedData = {}
    
    switch (job.type) {
      case 'sync.products':
      case 'sync.orders':
      case 'sync.customers':
        if (job.result.totalSynced !== undefined) {
          formattedData[t('Jobs.details.totalSynced')] = job.result.totalSynced
        }
        break
      
      case 'sync.markets':
        if (job.result.totalMarkets !== undefined) {
          formattedData[t('Jobs.details.totalMarkets')] = job.result.totalMarkets
        }
        break
      
      case 'relationship.products':
        if (job.result.totalProcessed !== undefined) {
          formattedData[t('Jobs.details.totalProcessed')] = job.result.totalProcessed
        }
        if (job.result.relationships !== undefined) {
          formattedData[t('Jobs.details.relationshipsGenerated')] = job.result.relationships
        }
        break
      
      default:
        // For unknown job types, show key fields if they exist
        if (job.result.total !== undefined) {
          formattedData[t('Jobs.details.total')] = job.result.total
        }
        if (job.result.count !== undefined) {
          formattedData[t('Jobs.details.resultCount')] = job.result.count
        }
        if (job.result.success !== undefined) {
          formattedData[t('Jobs.details.success')] = job.result.success ? 'Yes' : 'No'
        }
    }
    
    return Object.keys(formattedData).length > 0 ? formattedData : null
  }

  // Format event data based on job type and event type
  const formatEventData = (event, jobType) => {
    if (!event.data || Object.keys(event.data).length === 0) return null
    
    // Job types that should not show event data at all
    const hideEventDataTypes = [
      'webhook.webPixelUpdate',
      'storefront.createAccessToken',
      'web_pixel',
      'storefront_token'
    ]
    
    if (hideEventDataTypes.includes(jobType)) {
      return null
    }
    
    // Format specific data based on job type and event message
    const data = event.data
    const formattedData = {}
    
    // For sync jobs, show only relevant counts
    if (jobType === 'sync.products' || jobType === 'sync.orders' || jobType === 'sync.customers') {
      if (data.totalSynced !== undefined) {
        formattedData[t('Jobs.details.totalSynced')] = data.totalSynced
      }
      // If it's a completion event, show the count from the message
      if (event.message && event.message.includes('Synced')) {
        const match = event.message.match(/Synced (\d+)/)
        if (match) {
          formattedData[t('Jobs.details.totalSynced')] = parseInt(match[1])
        }
      }
      return Object.keys(formattedData).length > 0 ? formattedData : null
    }
    
    // Don't show chainId in event data
    // Chain information is already shown in the job details
    
    // For relationship jobs
    if (jobType === 'relationship.products') {
      if (data.totalProcessed !== undefined) {
        formattedData[t('Jobs.details.totalProcessed')] = data.totalProcessed
      }
      if (data.relationships !== undefined) {
        formattedData[t('Jobs.details.relationshipsGenerated')] = data.relationships
      }
      return Object.keys(formattedData).length > 0 ? formattedData : null
    }
    
    // For error events, show error details in a cleaner format
    if (event.eventType === 'FAILED' && data.errors) {
      if (data.errors.code) {
        formattedData[t('Jobs.details.errorCode')] = data.errors.code
      }
      if (data.errors.message) {
        formattedData[t('Jobs.details.errorMessage')] = data.errors.message
      }
      return Object.keys(formattedData).length > 0 ? formattedData : null
    }
    
    // Don't show raw data for other cases
    return null
  }

  // Check if job is in active state
  const isJobActive = job && (
    job.status === JobStatus.PENDING || 
    job.status === JobStatus.RUNNING || 
    job.status === JobStatus.STARTED
  )

  // Auto-refresh only when job is active
  useEffect(() => {
    if (isJobActive) {
      setRefreshInterval(15000) // Refresh every 15 seconds when job is active
    } else {
      setRefreshInterval(null) // Stop refreshing when job is complete
    }
    
    // Cleanup interval when component unmounts
    return () => {
      setRefreshInterval(null)
    }
  }, [isJobActive])

  const getStatusBadge = (status) => {
    const config = JobStatusConfig[status]
    if (config) {
      return <Badge tone={config.tone} size="large">{t(config.translationKey)}</Badge>
    }
    return <Badge tone="info" size="large">{status}</Badge>
  }

  const formatDate = (date) => {
    if (!date) return { relative: '-', exact: '-' }
    // Ensure we're working with a Date object in local timezone
    const dateObj = new Date(date)
    return {
      relative: formatDistance(dateObj, new Date(), { addSuffix: true }),
      // Format in user's local timezone
      exact: format(dateObj, 'PPpp') // This will use local timezone by default
    }
  }

  const DateWithTooltip = ({ date, label }) => {
    const formatted = formatDate(date)
    return (
      <Box>
        <Text variant="bodyMd" fontWeight="semibold">{label}</Text>
        <Text variant="bodySm" tone="subdued">{formatted.relative}</Text>
        <Text variant="bodySm" tone="subdued">{formatted.exact}</Text>
      </Box>
    )
  }

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'COMPLETED':
        return <CheckIcon />
      case 'FAILED':
        return <XIcon />
      default:
        return null
    }
  }

  const getEventBadgeTone = (eventType) => {
    switch (eventType) {
      case 'CREATED':
        return 'info'
      case 'STARTED':
        return 'attention'
      case 'COMPLETED':
        return 'success'
      case 'FAILED':
        return 'critical'
      case 'INFO':
        return 'info'
      default:
        return 'info'
    }
  }

  const getEventTypeLabel = (eventType) => {
    const translationKey = `Jobs.eventTypes.${eventType.toLowerCase()}`
    const translated = t(translationKey)
    if (translated !== translationKey) {
      return translated
    }
    // Fallback for any untranslated event types
    return eventType.charAt(0).toUpperCase() + eventType.slice(1).toLowerCase()
  }

  const canRetrigger = job && (job.status === JobStatus.FAILED || job.status === JobStatus.PENDING)

  if (isLoading) {
    return (
      <Page
        backAction={{ content: t('Jobs.details.back'), onAction: () => navigate('/settings/jobs') }}
        title={<SkeletonDisplayText size="large" />}
      >
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={5} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  if (error || !job) {
    return (
      <Page
        backAction={{ content: t('Jobs.details.back'), onAction: () => navigate('/settings/jobs') }}
        title={t('Jobs.details.errorTitle')}
      >
        <Card>
          <Text>{error?.message || t('Jobs.details.notFound')}</Text>
        </Card>
      </Page>
    )
  }

  const toastMarkup = toastActive ? (
    <Toast 
      content={toastMessage} 
      onDismiss={() => setToastActive(false)}
      error={toastError}
    />
  ) : null

  return (
    <Page
      backAction={{ content: t('Jobs.details.back'), onAction: () => navigate('/settings/jobs') }}
      title={
        isJobActive ? (
          <InlineStack gap="200">
            <Text as="span" variant="headingLg">{job.name}</Text>
            <Badge tone="warning">{t('Jobs.autoUpdating')}</Badge>
          </InlineStack>
        ) : job.name
      }
      subtitle={formatJobType(job.type)}
      compactTitle
    >
      <Layout>
        {/* Job Status and Progress */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd">{t('Jobs.details.status')}</Text>
                {getStatusBadge(job.status)}
              </InlineStack>
              
              {job.progress !== undefined && job.progress !== null && (
                <Box>
                  <ProgressBar progress={job.progress} />
                  <Box paddingBlockStart="200">
                    <Text variant="bodySm" tone="subdued">
                      {t('Jobs.details.progressText', { progress: job.progress })}
                    </Text>
                  </Box>
                </Box>
              )}
              
              {job.currentStep && job.totalSteps && (
                <Text variant="bodySm" tone="subdued">
                  {t('Jobs.details.count', { current: job.currentStep, total: job.totalSteps })}
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Job Information */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd">{t('Jobs.details.information')}</Text>
              <Divider />
              <InlineGrid columns="1fr 1fr 1fr" gap="400">
                <DateWithTooltip date={job.createdAt} label={t('Jobs.details.created')} />
                <DateWithTooltip date={job.startedAt} label={t('Jobs.details.started')} />
                <DateWithTooltip date={job.completedAt} label={t('Jobs.details.completed')} />
              </InlineGrid>
              
              {job.retryCount > 0 && (
                <>
                  <Divider />
                  <InlineStack gap="400">
                    <Text variant="bodyMd" fontWeight="semibold">{t('Jobs.details.retries')}</Text>
                    <Text variant="bodySm">{job.retryCount} / {job.maxRetries}</Text>
                  </InlineStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>


        {/* Job Results */}
        {(() => {
          const formattedResult = formatJobResult(job)
          if (!formattedResult) return null
          
          return (
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd">{t('Jobs.details.results')}</Text>
                  <Divider />
                  <BlockStack gap="200">
                    {Object.entries(formattedResult).map(([key, value]) => (
                      <InlineStack key={key} gap="400">
                        <Text variant="bodyMd" fontWeight="semibold">{key}:</Text>
                        <Text variant="bodyMd">{value}</Text>
                      </InlineStack>
                    ))}
                  </BlockStack>
                </BlockStack>
              </Card>
            </Layout.Section>
          )
        })()}

        {/* Errors */}
        {job.errors && Object.keys(job.errors).length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" tone="critical">{t('Jobs.details.errors')}</Text>
                <Divider />
                <Box>
                  <pre style={{ 
                    backgroundColor: '#fef2f2', 
                    padding: '12px', 
                    borderRadius: '8px',
                    overflow: 'auto',
                    fontSize: '12px',
                    color: '#991b1b'
                  }}>
                    {JSON.stringify(job.errors, null, 2)}
                  </pre>
                </Box>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Events Timeline */}
        {events.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd">{t('Jobs.details.events')}</Text>
                <Divider />
                <BlockStack gap="300">
                  {[...events].reverse().map((event, index) => (
                    <Box key={event.id}>
                      <InlineStack gap="300" blockAlign="start">
                        <Box width="24px">
                          {getEventIcon(event.eventType)}
                        </Box>
                        <Box minWidth="0" width="100%">
                          <InlineStack align="space-between">
                            <BlockStack gap="100">
                              <Text variant="bodyMd" fontWeight="semibold">
                                {event.message}
                              </Text>
                              <Text variant="bodySm" tone="subdued">
                                {formatDate(event.createdAt).relative}
                              </Text>
                            </BlockStack>
                            <Badge tone={getEventBadgeTone(event.eventType)}>
                              {getEventTypeLabel(event.eventType)}
                            </Badge>
                          </InlineStack>
                          {(() => {
                            const eventData = formatEventData(event, job.type)
                            if (!eventData) return null
                            
                            return (
                              <Box paddingBlockStart="200">
                                <Box style={{ 
                                  backgroundColor: '#f6f6f7', 
                                  padding: '8px', 
                                  borderRadius: '4px'
                                }}>
                                  <BlockStack gap="100">
                                    {Object.entries(eventData).map(([key, value]) => (
                                      <InlineStack key={key} gap="200">
                                        <Text variant="bodySm" fontWeight="medium">{key}:</Text>
                                        <Text variant="bodySm">{value}</Text>
                                      </InlineStack>
                                    ))}
                                  </BlockStack>
                                </Box>
                              </Box>
                            )
                          })()}
                        </Box>
                      </InlineStack>
                      {index < [...events].reverse().length - 1 && (
                        <Box paddingBlockStart="300">
                          <Divider />
                        </Box>
                      )}
                    </Box>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>
      {toastMarkup}
    </Page>
  )
}